// src/pages/DebtStudio/components/Reports/index.jsx

import React, { useState, useEffect, useMemo } from "react";
import SectionCard from "components/SectionCard";
import TabMenu from "components/TabMenu";
import PdfExportMenu from "components/PdfExportMenu";

import SourcesUsesReport from "./SourcesUsesReport";
import StatsReport from "./StatsReport";
import BondSummaryReport from "./BondSummaryReport";
import DebtServiceReport from "./DebtServiceReport";
import CostOfIssuanceReport from "./CostOfIssuanceReport";
import CapIReport from "./CapIReport";

import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import { buildDebtServiceSchedule } from "pages/DebtStudio/lib/buildDebtServiceSchedule";
import { buildSourcesUses } from "pages/DebtStudio/lib/buildSourcesUses";
import { buildCostOfIssuanceReport } from "pages/DebtStudio/lib/buildCostOfIssuanceReport";
import { computeCapI } from "pages/DebtStudio/lib/computeCapI";
import { buildWrapBaseByPrincipalPeriods } from "pages/DebtStudio/lib/buildWrapBase";
import { deriveLdsPrincipalEndDates } from "pages/DebtStudio/components/Issues/DealStructure/hooks/ldsSolver";
import { trunc3 } from "pages/DebtStudio/utils/formatters";
import {
  bucketCashflowsByPeriod,
  buildPriorDebtServiceCashflows,
} from "pages/DebtStudio/lib/refundingSavings";

import exportDebtStudioIssuePdf from "pages/DebtStudio/reports/exportDebtStudioIssuePdf";
import { buildIssueReportData } from "pages/DebtStudio/reports/buildIssueReportData";

const BASE_TABS = [
  { key: "sources", label: "Sources & Uses" },
  { key: "stats", label: "Stats" },
  { key: "summary", label: "Bond Summary" },
  { key: "debt", label: "Debt Service" },
  { key: "coi", label: "Cost of Issuance" },
];

const PDF_SELECTION_KEY = "debtstudio.issue.pdfReports.v1";

function loadPdfSelection() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PDF_SELECTION_KEY) || "null");
    return Array.isArray(parsed) ? parsed : null;
  } catch (_err) {
    return null;
  }
}

/** Normalize form shape and keys for Reports */
function normalizeForm(form = {}) {
  return {
    ...form,
    costOfIssuance: form.costOfIssuance ?? form.cost_of_issuance ?? [],
    parAmount: form.parAmount ?? form.par_amount ?? 0,
    targetType: form.targetType ?? form.target_type ?? "par_amount",
    targetValue:
      form.targetValue ??
      form.target_value ??
      form.parAmount ??
      form.par_amount ??
      0,
    denomination: form.denomination ?? 5000,
    datedDate: form.datedDate ?? form.dated_date ?? "",
    deliveryDate: form.deliveryDate ?? form.delivery_date ?? "",
    finalMaturityDate:
      form.finalMaturityDate ?? form.final_maturity_date ?? "",
    firstCouponDate:
      form.firstCouponDate ?? form.first_coupon_date ?? "",
    firstPrincipalDate:
      form.firstPrincipalDate ?? form.first_principal_date ?? "",
    fiscalYearEnd:
      form.fiscalYearEnd ?? form.fiscal_year_end ?? "",
    dayCount:
      form.dayCount ??
      form.day_count ??
      form.day_count_convention ??
      "30/360",
    taxStatus: form.taxStatus ?? form.tax_status ?? null,

    financingType:
      form.financingType ??
      form.financing_type ??
      "bond",

    structureMode:
      form.structureMode ?? form.structure_mode ?? "",

    principalFrequency:
      form.principalFrequency ??
      form.principal_frequency ??
      "annual",

    wrapManualCashflows:
      form.wrapManualCashflows ??
      form.wrap_manual_cashflows ??
      [],

    // CapI fields (normalize to the UI keys CapIReport expects)
    dealType: form.dealType ?? form.deal_type ?? "",
    capiEnabled:
      form.capiEnabled ?? form.capi_enabled ?? false,
    capiDate: form.capiDate ?? form.capi_date ?? "",
    capiInvestmentRate:
      form.capiInvestmentRate ??
      form.capi_investment_rate ??
      0,
  };
}

function isCapiApplicable(form) {
  const dealTypeStr = String(
    form?.dealType || form?.deal_type || ""
  )
    .toLowerCase()
    .trim();

  const enabled = !!(
    form?.capiEnabled ?? form?.capi_enabled
  );

  const applicable =
    dealTypeStr === "new_money" ||
    dealTypeStr === "combined" ||
    dealTypeStr === "combination";

  return enabled && applicable;
}

export default function Reports() {
  const {
    form,
    structureRows,
    ensureStructure,
    solution,
    selectedOrg,
    scale,
    benchmarkCurveId,
    interpMethod,
  } = useDebtStudio();

  const normalizedForm = useMemo(
    () => normalizeForm(form),
    [form]
  );

  const isLoan = useMemo(
    () =>
      String(normalizedForm?.financingType || "")
        .trim()
        .toLowerCase() === "loan",
    [normalizedForm?.financingType]
  );

  const capiOn = useMemo(
    () => isCapiApplicable(normalizedForm),
    [normalizedForm]
  );
  const isRefunding = String(normalizedForm?.dealType || "").toLowerCase() === "refunding";

  const isLibraryIssue = useMemo(
    () =>
      String(form?.dealClass || "").toLowerCase() ===
      "library",
    [form?.dealClass]
  );

  const [activeTab, setActiveTab] = useState(() =>
    String(form?.dealClass || "").toLowerCase() ===
    "library"
      ? "summary"
      : "sources"
  );

  const [isPdfGenerating, setIsPdfGenerating] =
    useState(false);
  const [pdfReports, setPdfReports] = useState(loadPdfSelection);
  const [expandedCoiCategories, setExpandedCoiCategories] = useState(
    () => new Set()
  );

  const toggleCoiCategory = (label) => {
    setExpandedCoiCategories((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  useEffect(() => {
    if (form?.activeTab?.startsWith("Reports")) {
      const subTab =
        form.activeTab.split(":")[1] || "sources";
      setActiveTab(subTab);
    }
  }, [form?.activeTab]);

  useEffect(() => {
    if (!form || structureRows?.length) return;
    ensureStructure();
  }, [form, structureRows, ensureStructure]);

  const derivedRows = useMemo(() => {
    if (!Array.isArray(structureRows)) return [];

    return structureRows.map((r) => ({
      ...r,
      price:
        r.price != null && !isNaN(r.price)
          ? Number(r.price)
          : null,
      yield:
        r.yield != null && !isNaN(r.yield)
          ? Number(r.yield)
          : null,
      coupon:
        r.coupon != null && !isNaN(r.coupon)
          ? Number(r.coupon)
          : null,
      principal:
        r.principal != null && !isNaN(r.principal)
          ? Number(r.principal)
          : null,
    }));
  }, [structureRows]);

  const finalPar = useMemo(
    () =>
      derivedRows.reduce(
        (sum, row) => sum + (Number(row?.principal) || 0),
        0
      ),
    [derivedRows]
  );

  const hasCostOfIssuance = useMemo(
    () =>
      buildCostOfIssuanceReport(
        normalizedForm.costOfIssuance,
        finalPar
      ).rows.length > 0,
    [normalizedForm.costOfIssuance, finalPar]
  );

  const baseDebtServiceByPeriod = useMemo(() => {
    const savedCandidates = [
      solution?.baseDebtServiceByPeriod,
      solution?.base_debt_service_by_bond_year,
      solution?.wrapBaseDebtServiceByBondYear,
      solution?.wrap_base_debt_service_by_bond_year,
    ];

    const savedBase =
      savedCandidates.find(
        (v) => Array.isArray(v) && v.length > 0
      ) || [];

    if (savedBase.length > 0) return savedBase;

    const isWrapMode =
      String(
        normalizedForm?.structureMode ??
          normalizedForm?.structure_mode ??
          ""
      ).toLowerCase() === "wrap_lds";

    if (!isWrapMode) return [];

    const existingWrapCashflows = Array.isArray(
      normalizedForm?.wrapManualCashflows
    )
      ? normalizedForm.wrapManualCashflows
      : Array.isArray(
          normalizedForm?.wrap_manual_cashflows
        )
      ? normalizedForm.wrap_manual_cashflows
      : [];

    if (!existingWrapCashflows.length) return [];
    if (!Array.isArray(derivedRows) || !derivedRows.length)
      return [];

    const principalEndDates =
      deriveLdsPrincipalEndDates({
        rows: derivedRows,
        firstPrincipalDate:
          normalizedForm.firstPrincipalDate,
        finalMaturityDate:
          normalizedForm.finalMaturityDate,
        principalFrequency:
          normalizedForm.principalFrequency,
      });

    const built = buildWrapBaseByPrincipalPeriods({
      principalEndDates,
      cashflows: existingWrapCashflows,
      startDate:
        normalizedForm.deliveryDate ||
        normalizedForm.datedDate,
    });

    return Array.isArray(built?.baseByBondYear)
      ? built.baseByBondYear
      : [];
  }, [solution, normalizedForm, derivedRows]);

  const tabs = useMemo(() => {
    let t = [...BASE_TABS];

    if (isLibraryIssue) {
      t = t.filter(
        (tab) =>
          tab.key !== "sources" &&
          tab.key !== "stats" &&
          tab.key !== "coi"
      );
    }

    if (isLoan) {
      t = t.filter((tab) => tab.key !== "summary");
    }

    if (!hasCostOfIssuance) {
      t = t.filter((tab) => tab.key !== "coi");
    }

    if (capiOn)
      t.push({ key: "capi", label: "Cap-I" });
    return t;
  }, [capiOn, hasCostOfIssuance, isLibraryIssue, isLoan]);
  const pdfReportOptions = useMemo(
    () => tabs.map((tab) => ({ value: tab.key, label: tab.label })),
    [tabs]
  );
  const availablePdfReportKeys = useMemo(
    () => pdfReportOptions.map((option) => option.value),
    [pdfReportOptions]
  );
  const selectedPdfReports = useMemo(
    () =>
      pdfReports == null
        ? availablePdfReportKeys
        : availablePdfReportKeys.filter((key) => pdfReports.includes(key)),
    [availablePdfReportKeys, pdfReports]
  );

  const handlePdfReportsChange = (values) => {
    setPdfReports(values);
    try {
      sessionStorage.setItem(PDF_SELECTION_KEY, JSON.stringify(values));
    } catch (_err) {
      // Session storage may be unavailable; retain the in-memory selection.
    }
  };

  useEffect(() => {
    const allowedKeys = new Set(
      tabs.map((tab) => tab.key)
    );

    if (!allowedKeys.has(activeTab)) {
      if (allowedKeys.has("sources")) {
        setActiveTab("sources");
      } else if (allowedKeys.has("debt")) {
        setActiveTab("debt");
      } else if (allowedKeys.has("summary")) {
        setActiveTab("summary");
      } else if (tabs.length) {
        setActiveTab(tabs[0].key);
      }
    }
  }, [activeTab, tabs]);

  // If CapI toggles off while user is on the tab, bounce them to a visible default
  useEffect(() => {
    if (activeTab === "capi" && !capiOn) {
      if (!isLibraryIssue) {
        setActiveTab("sources");
      } else if (!isLoan) {
        setActiveTab("summary");
      } else {
        setActiveTab("debt");
      }
    }
  }, [activeTab, capiOn, isLibraryIssue, isLoan]);

  const capi = useMemo(() => {
    if (
      !normalizedForm ||
      !derivedRows?.length ||
      !capiOn
    )
      return null;

    try {
      return computeCapI({
        dealType:
          normalizedForm?.dealType ||
          normalizedForm?.deal_type,
        capiEnabled:
          normalizedForm?.capiEnabled ??
          normalizedForm?.capi_enabled ??
          false,
        capiDate:
          normalizedForm?.capiDate ??
          normalizedForm?.capi_date ??
          "",
        capiInvestmentRate:
          normalizedForm?.capiInvestmentRate ??
          normalizedForm?.capi_investment_rate ??
          0,
        rows: derivedRows,
        form: normalizedForm,
      });
    } catch (err) {
      console.warn(
        "Reports: computeCapI failed",
        err
      );
      return null;
    }
  }, [derivedRows, normalizedForm, capiOn]);

  const schedule = useMemo(() => {
    if (!normalizedForm || !derivedRows?.length)
      return [];

    try {
      const built = buildDebtServiceSchedule({
        rows: derivedRows,
        form: normalizedForm,
        isLibraryIssue,
        debtServiceAdjustments:
          capi?.debtServiceAdjustments || [],
        baseDebtServiceByPeriod,
      });

      return built;
    } catch (err) {
      console.warn(
        "Reports: buildDebtServiceSchedule failed",
        err
      );
      return [];
    }
  }, [
    derivedRows,
    normalizedForm,
    isLibraryIssue,
    capi,
    baseDebtServiceByPeriod,
  ]);

  const refundingComparison = useMemo(() => {
    if (!isRefunding || !derivedRows.length) return [];
    const entries = Array.isArray(normalizedForm?.refunded_issues)
      ? normalizedForm.refunded_issues.filter((entry) => entry?.entry_type === "ds_issue")
      : [];
    if (!entries.length) return [];
    const periodEnds = derivedRows.map((row) => row.date);
    const startDate = normalizedForm.deliveryDate || normalizedForm.datedDate;
    const prior = bucketCashflowsByPeriod(
      buildPriorDebtServiceCashflows(entries, startDate), periodEnds, startDate
    );
    const proposed = bucketCashflowsByPeriod(
      schedule.map((row) => ({ date: row.date, total: row.netDebtService ?? row.total })),
      periodEnds,
      startDate
    );
    return periodEnds.map((date, index) => ({
      date,
      priorDebtService: prior[index] || 0,
      refundingDebtService: proposed[index] || 0,
      savings: (prior[index] || 0) - (proposed[index] || 0),
    }));
  }, [isRefunding, normalizedForm, derivedRows, schedule]);

  const sourcesUses = useMemo(() => {
    if (!normalizedForm || !derivedRows?.length)
      return null;

    try {
      return buildSourcesUses(
        normalizedForm,
        derivedRows
      );
    } catch (err) {
      console.warn(
        "Reports: buildSourcesUses failed",
        err
      );
      return null;
    }
  }, [normalizedForm, derivedRows]);

  // Bond Summary data (same as current Reports/index.jsx intent)
  const bondSummaryData = useMemo(() => {
    if (!derivedRows?.length) return [];

    return derivedRows.map((r) => ({
      ...r,
      price:
        Number.isFinite(r.price)
          ? trunc3(r.price)
          : null,
      yield:
        Number.isFinite(r.yield)
          ? Number(r.yield.toFixed(3))
          : null,
      coupon:
        Number.isFinite(r.coupon)
          ? Number(r.coupon.toFixed(3))
          : null,
      principal:
        Number.isFinite(r.principal)
          ? r.principal
          : null,
    }));
  }, [derivedRows]);

  const onPdfExport = async () => {
    try {
      setIsPdfGenerating(true);

      const pdfSourcesUses = sourcesUses
        ? {
            ...sourcesUses,
            uses: (sourcesUses.uses || []).map((row) => ({
              ...row,
              expanded: expandedCoiCategories.has(row?.label),
            })),
          }
        : sourcesUses;

      const reportData = buildIssueReportData({
        form: normalizedForm,
        rows: derivedRows,
        debtServiceRows: schedule,
        refundingComparison,
        sourcesUses: pdfSourcesUses,
        stats: null,
        solution: {
          ...solution,
          scale,
          benchmarkCurveId,
          interpMethod,
        },
        orgName: selectedOrg?.name || "",
        issueName:
          normalizedForm?.dealName ||
          normalizedForm?.name ||
          "",
      });

      await exportDebtStudioIssuePdf({
        reportData,
        orgId: selectedOrg?.id,
        selectedReports: selectedPdfReports,
      });
    } catch (err) {
      console.warn(
        "DebtStudio Issue PDF export failed:",
        err
      );
      alert(
        `PDF export failed: ${
          err?.message || String(err)
        }`
      );
    } finally {
      setIsPdfGenerating(false);
    }
  };

  if (!form) return null;

  return (
    <>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <TabMenu
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </div>

        <PdfExportMenu
          options={pdfReportOptions}
          values={selectedPdfReports}
          onChange={handlePdfReportsChange}
          onExport={onPdfExport}
          disabled={!derivedRows?.length}
          generating={isPdfGenerating}
          title="Choose DebtStudio reports to export to PDF"
        />
      </div>

      {activeTab === "sources" &&
        !isLibraryIssue && (
          <SectionCard title="Sources & Uses">
            <SourcesUsesReport
              data={sourcesUses}
              expandedCategories={expandedCoiCategories}
              onToggleCategory={toggleCoiCategory}
            />
          </SectionCard>
        )}

      {activeTab === "stats" &&
        !isLibraryIssue && (
          <SectionCard title="Statistics">
            <StatsReport
              bondRows={derivedRows}
              flowRows={schedule}
              refundingComparison={refundingComparison}
            />
          </SectionCard>
        )}

      {activeTab === "summary" && !isLoan && (
        <SectionCard title="Bond Summary">
          <BondSummaryReport
            rows={bondSummaryData}
          />
        </SectionCard>
      )}

      {activeTab === "debt" && (
        <DebtServiceReport
          rows={schedule}
          form={normalizedForm}
          refundingComparison={refundingComparison}
        />
      )}

      {activeTab === "coi" && !isLibraryIssue && hasCostOfIssuance && (
        <SectionCard title="Cost of Issuance">
          <CostOfIssuanceReport
            items={normalizedForm.costOfIssuance}
            parAmount={finalPar}
          />
        </SectionCard>
      )}


      {activeTab === "capi" && capiOn && (
        <CapIReport
          form={normalizedForm}
          rows={derivedRows}
        />
      )}
    </>
  );
}
